<template>
  <div class="asset-telemetry-dashboard">
    <!-- Header -->
    <Card class="mb-3">
      <template #content>
        <div class="flex justify-content-between align-items-center">
          <div>
            <h3 class="mb-2">{{ asset?.asset_name || $translate('asset_telemetry') }}</h3>
            <div class="flex align-items-center gap-3">
              <div v-if="device">
                <i class="pi pi-microchip mr-2"></i>
                <span class="font-mono">{{ device.mac_address }}</span>
              </div>
              <Tag v-if="device" :severity="getStatusSeverity(device.status)" :value="$translate(device.status)" />
            </div>
          </div>
          <div class="flex gap-2">
            <Button
              :label="$translate('export')"
              icon="pi pi-download"
              class="p-button-outlined"
              @click="exportData"
            />
            <Button
              :label="$translate('refresh')"
              icon="pi pi-refresh"
              class="p-button-outlined"
              @click="fetchData"
              :loading="loading"
            />
          </div>
        </div>
      </template>
    </Card>

    <!-- Time Range Selector -->
    <Card class="mb-3">
      <template #content>
        <div class="grid align-items-end">
          <div class="col-12 md:col-3">
            <label>{{ $translate('time_range') }}</label>
            <Dropdown
              v-model="timeRange"
              :options="timeRangeOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              @change="onTimeRangeChange"
            />
          </div>

          <div v-if="timeRange === 'custom'" class="col-12 md:col-3">
            <label>{{ $translate('start_date') }}</label>
            <Calendar
              v-model="customStartDate"
              class="w-full"
              dateFormat="yy-mm-dd"
              :showTime="true"
            />
          </div>

          <div v-if="timeRange === 'custom'" class="col-12 md:col-3">
            <label>{{ $translate('end_date') }}</label>
            <Calendar
              v-model="customEndDate"
              class="w-full"
              dateFormat="yy-mm-dd"
              :showTime="true"
            />
          </div>

          <div v-if="timeRange === 'custom'" class="col-12 md:col-3">
            <Button
              :label="$translate('apply')"
              icon="pi pi-check"
              class="w-full"
              @click="fetchData"
            />
          </div>

          <div class="col-12 md:col-3">
            <div class="field-checkbox">
              <Checkbox v-model="autoRefresh" inputId="autoRefresh" :binary="true" @change="toggleAutoRefresh" />
              <label for="autoRefresh" class="ml-2">{{ $translate('auto_refresh') }}</label>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Current Status Cards -->
    <div class="grid mb-3">
      <div v-if="latestReading" class="col-12 md:col-3" v-for="metric in visibleMetrics" :key="metric.key">
        <Card :class="['status-card', getMetricStatusClass(metric.key, latestReading[metric.key])]">
          <template #content>
            <div class="flex justify-content-between align-items-center">
              <div>
                <div class="text-500 font-medium mb-2">{{ $translate(metric.label) }}</div>
                <div class="text-900 font-bold text-2xl">
                  {{ formatMetricValue(metric.key, latestReading[metric.key]) }}
                </div>
                <div class="text-500 text-sm mt-1">
                  {{ formatDateTime(latestReading.recorded_at) }}
                </div>
              </div>
              <i :class="metric.icon" :style="{fontSize: '2.5rem', color: metric.color}"></i>
            </div>
          </template>
        </Card>
      </div>

      <div v-if="!latestReading && !loading" class="col-12">
        <Message severity="info" :closable="false">
          {{ $translate('no_telemetry_data_available') }}
        </Message>
      </div>
    </div>

    <!-- Charts -->
    <div v-if="telemetryData.length > 0" class="grid">
      <!-- Temperature Chart -->
      <div v-if="hasMetric('temperature')" class="col-12 lg:col-6">
        <Card>
          <template #title>
            <div class="text-base">{{ $translate('temperature_trend') }}</div>
          </template>
          <template #content>
            <div class="chart-container">
              <canvas ref="temperatureChart"></canvas>
            </div>
          </template>
        </Card>
      </div>

      <!-- Humidity Chart -->
      <div v-if="hasMetric('humidity')" class="col-12 lg:col-6">
        <Card>
          <template #title>
            <div class="text-base">{{ $translate('humidity_trend') }}</div>
          </template>
          <template #content>
            <div class="chart-container">
              <canvas ref="humidityChart"></canvas>
            </div>
          </template>
        </Card>
      </div>

      <!-- Battery Chart -->
      <div v-if="hasMetric('battery_level')" class="col-12 lg:col-6">
        <Card>
          <template #title>
            <div class="text-base">{{ $translate('battery_level_trend') }}</div>
          </template>
          <template #content>
            <div class="chart-container">
              <canvas ref="batteryChart"></canvas>
            </div>
          </template>
        </Card>
      </div>

      <!-- RSSI Chart -->
      <div v-if="hasMetric('rssi')" class="col-12 lg:col-6">
        <Card>
          <template #title>
            <div class="text-base">{{ $translate('signal_strength_trend') }}</div>
          </template>
          <template #content>
            <div class="chart-container">
              <canvas ref="rssiChart"></canvas>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Telemetry History Table -->
    <Card class="mt-3">
      <template #title>
        <div class="text-base">{{ $translate('telemetry_history') }}</div>
      </template>
      <template #content>
        <DataTable
          :value="telemetryData"
          :loading="loading"
          :rows="10"
          :rowsPerPageOptions="[10, 20, 50]"
          :paginator="true"
          responsiveLayout="scroll"
          stripedRows
          :rowHover="true"
        >
          <template #empty>
            <div class="text-center p-4">
              <i class="pi pi-inbox" style="font-size: 3rem; color: #cbd5e0;"></i>
              <p class="mt-3 text-500">{{ $translate('no_data_found') }}</p>
            </div>
          </template>

          <Column field="recorded_at" :header="$translate('timestamp')" :sortable="true">
            <template #body="{ data }">
              {{ formatFullDateTime(data.recorded_at) }}
            </template>
          </Column>

          <Column v-if="hasMetric('temperature')" field="temperature" :header="$translate('temperature')" :sortable="true">
            <template #body="{ data }">
              <span :class="getAlertClass('temp_alert', data.temp_alert)">
                {{ data.temperature ? data.temperature.toFixed(1) + '°C' : '-' }}
              </span>
              <i v-if="data.temp_alert" class="pi pi-exclamation-triangle text-red-500 ml-2"></i>
            </template>
          </Column>

          <Column v-if="hasMetric('humidity')" field="humidity" :header="$translate('humidity')" :sortable="true">
            <template #body="{ data }">
              <span :class="getAlertClass('humidity_alert', data.humidity_alert)">
                {{ data.humidity ? data.humidity.toFixed(1) + '%' : '-' }}
              </span>
              <i v-if="data.humidity_alert" class="pi pi-exclamation-triangle text-red-500 ml-2"></i>
            </template>
          </Column>

          <Column v-if="hasMetric('battery_level')" field="battery_level" :header="$translate('battery')" :sortable="true">
            <template #body="{ data }">
              <div v-if="data.battery_level !== null" class="flex align-items-center gap-2">
                <ProgressBar
                  :value="data.battery_level"
                  :showValue="false"
                  style="height: 8px; width: 60px;"
                  :class="getBatteryClass(data.battery_level)"
                />
                <span>{{ data.battery_level }}%</span>
              </div>
              <span v-else>-</span>
            </template>
          </Column>

          <Column v-if="hasMetric('rssi')" field="rssi" :header="$translate('rssi')" :sortable="true">
            <template #body="{ data }">
              {{ data.rssi ? data.rssi + ' dBm' : '-' }}
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<script>
import iotApi from '@/api/iot'
import Chart from 'chart.js'

export default {
  name: 'AssetTelemetryDashboard',
  props: {
    assetId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      asset: null,
      device: null,
      telemetryData: [],
      latestReading: null,
      loading: false,
      autoRefresh: false,
      refreshInterval: null,
      timeRange: '24h',
      customStartDate: null,
      customEndDate: null,
      charts: {
        temperature: null,
        humidity: null,
        battery: null,
        rssi: null
      },
      timeRangeOptions: [
        { label: this.$translate('last_hour'), value: '1h' },
        { label: this.$translate('last_6_hours'), value: '6h' },
        { label: this.$translate('last_24_hours'), value: '24h' },
        { label: this.$translate('last_7_days'), value: '7d' },
        { label: this.$translate('last_30_days'), value: '30d' },
        { label: this.$translate('custom_range'), value: 'custom' }
      ],
      metrics: [
        { key: 'temperature', label: 'temperature', icon: 'pi pi-thermometer', color: '#ef4444', unit: '°C' },
        { key: 'humidity', label: 'humidity', icon: 'pi pi-cloud', color: '#3b82f6', unit: '%' },
        { key: 'battery_level', label: 'battery', icon: 'pi pi-bolt', color: '#22c55e', unit: '%' },
        { key: 'rssi', label: 'signal_strength', icon: 'pi pi-wifi', color: '#f59e0b', unit: ' dBm' }
      ]
    }
  },
  computed: {
    visibleMetrics() {
      return this.metrics.filter(m => this.hasMetric(m.key))
    }
  },
  mounted() {
    this.fetchData()
  },
  beforeDestroy() {
    this.destroyCharts()
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        // Fetch asset details
        // const assetResponse = await assetApi.fetchById(this.assetId)
        // this.asset = assetResponse.data.data

        // Fetch telemetry data
        const params = {}

        if (this.timeRange !== 'custom') {
          params.time_range = this.timeRange
        } else {
          if (this.customStartDate) params.start_date = this.formatDateForAPI(this.customStartDate)
          if (this.customEndDate) params.end_date = this.formatDateForAPI(this.customEndDate)
        }

        const [telemetryResponse, latestResponse] = await Promise.all([
          iotApi.fetchAssetTelemetry(this.assetId, params),
          iotApi.fetchLatestTelemetry(this.assetId)
        ])

        this.telemetryData = telemetryResponse.data.data
        this.latestReading = latestResponse.data.data
        this.device = this.latestReading?.device

        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$translate('error'),
          detail: error.response?.data?.message || this.$translate('failed_to_fetch_telemetry'),
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },

    hasMetric(metricKey) {
      return this.latestReading && this.latestReading[metricKey] !== null && this.latestReading[metricKey] !== undefined
    },

    renderCharts() {
      this.destroyCharts()

      const chartData = this.prepareChartData()

      if (this.hasMetric('temperature') && this.$refs.temperatureChart) {
        this.charts.temperature = this.createChart(
          this.$refs.temperatureChart,
          'temperature',
          this.$translate('temperature'),
          chartData.temperature,
          '#ef4444'
        )
      }

      if (this.hasMetric('humidity') && this.$refs.humidityChart) {
        this.charts.humidity = this.createChart(
          this.$refs.humidityChart,
          'humidity',
          this.$translate('humidity'),
          chartData.humidity,
          '#3b82f6'
        )
      }

      if (this.hasMetric('battery_level') && this.$refs.batteryChart) {
        this.charts.battery = this.createChart(
          this.$refs.batteryChart,
          'battery_level',
          this.$translate('battery'),
          chartData.battery_level,
          '#22c55e'
        )
      }

      if (this.hasMetric('rssi') && this.$refs.rssiChart) {
        this.charts.rssi = this.createChart(
          this.$refs.rssiChart,
          'rssi',
          this.$translate('signal_strength'),
          chartData.rssi,
          '#f59e0b'
        )
      }
    },

    prepareChartData() {
      const data = {
        temperature: { labels: [], values: [] },
        humidity: { labels: [], values: [] },
        battery_level: { labels: [], values: [] },
        rssi: { labels: [], values: [] }
      }

      this.telemetryData.forEach(reading => {
        const timestamp = this.formatChartLabel(reading.recorded_at)

        if (reading.temperature !== null) {
          data.temperature.labels.push(timestamp)
          data.temperature.values.push(reading.temperature)
        }

        if (reading.humidity !== null) {
          data.humidity.labels.push(timestamp)
          data.humidity.values.push(reading.humidity)
        }

        if (reading.battery_level !== null) {
          data.battery_level.labels.push(timestamp)
          data.battery_level.values.push(reading.battery_level)
        }

        if (reading.rssi !== null) {
          data.rssi.labels.push(timestamp)
          data.rssi.values.push(reading.rssi)
        }
      })

      return data
    },

    createChart(canvas, metric, label, data, color) {
      const ctx = canvas.getContext('2d')

      return new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [{
            label: label,
            data: data.values,
            borderColor: color,
            backgroundColor: color + '20',
            borderWidth: 2,
            tension: 0.4,
            fill: true,
            pointRadius: 3,
            pointHoverRadius: 5
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio: 2,
          legend: {
            display: false
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: true,
                color: '#e5e7eb'
              },
              ticks: {
                maxRotation: 45,
                minRotation: 0
              }
            }],
            yAxes: [{
              gridLines: {
                display: true,
                color: '#e5e7eb'
              },
              ticks: {
                beginAtZero: metric !== 'rssi'
              }
            }]
          },
          tooltips: {
            mode: 'index',
            intersect: false
          }
        }
      })
    },

    destroyCharts() {
      Object.values(this.charts).forEach(chart => {
        if (chart) {
          chart.destroy()
        }
      })
      this.charts = {
        temperature: null,
        humidity: null,
        battery: null,
        rssi: null
      }
    },

    onTimeRangeChange() {
      if (this.timeRange !== 'custom') {
        this.fetchData()
      }
    },

    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.fetchData()
        }, 30000) // Refresh every 30 seconds
      } else {
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval)
          this.refreshInterval = null
        }
      }
    },

    async exportData() {
      try {
        const params = {}
        if (this.timeRange !== 'custom') {
          params.time_range = this.timeRange
        } else {
          if (this.customStartDate) params.start_date = this.formatDateForAPI(this.customStartDate)
          if (this.customEndDate) params.end_date = this.formatDateForAPI(this.customEndDate)
        }

        const response = await iotApi.exportTelemetry(this.assetId, params)
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `telemetry-asset-${this.assetId}-${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$translate('error'),
          detail: this.$translate('export_failed'),
          life: 3000
        })
      }
    },

    formatMetricValue(key, value) {
      if (value === null || value === undefined) return '-'

      const metric = this.metrics.find(m => m.key === key)
      return value.toFixed(1) + (metric?.unit || '')
    },

    getMetricStatusClass(key, value) {
      if (value === null || value === undefined) return ''

      if (key === 'battery_level') {
        if (value < 20) return 'status-critical'
        if (value < 40) return 'status-warning'
        return 'status-good'
      }

      return ''
    },

    getStatusSeverity(status) {
      const severityMap = {
        active: 'success',
        inactive: 'warning',
        offline: 'danger',
        error: 'danger'
      }
      return severityMap[status] || 'info'
    },

    getAlertClass(alertField, hasAlert) {
      return hasAlert ? 'text-red-600 font-bold' : ''
    },

    getBatteryClass(level) {
      if (level >= 60) return 'battery-good'
      if (level >= 30) return 'battery-medium'
      return 'battery-low'
    },

    formatDateTime(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diff = Math.floor((now - date) / 1000)

      if (diff < 60) return `${diff}s ${this.$translate('ago')}`
      if (diff < 3600) return `${Math.floor(diff / 60)}m ${this.$translate('ago')}`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ${this.$translate('ago')}`

      return date.toLocaleString()
    },

    formatFullDateTime(dateString) {
      return new Date(dateString).toLocaleString()
    },

    formatChartLabel(dateString) {
      const date = new Date(dateString)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatDateForAPI(date) {
      return date.toISOString()
    }
  }
}
</script>

<style scoped>
.asset-telemetry-dashboard {
  padding: 20px;
}

.status-card {
  transition: transform 0.2s;
}

.status-card:hover {
  transform: translateY(-2px);
}

.status-good {
  border-left: 4px solid #22c55e;
}

.status-warning {
  border-left: 4px solid #f59e0b;
}

.status-critical {
  border-left: 4px solid #ef4444;
}

.chart-container {
  position: relative;
  height: 300px;
}

.font-mono {
  font-family: 'Courier New', monospace;
}

:deep(.battery-good .p-progressbar-value) {
  background: #22c55e;
}

:deep(.battery-medium .p-progressbar-value) {
  background: #f59e0b;
}

:deep(.battery-low .p-progressbar-value) {
  background: #ef4444;
}
</style>